import { autorun } from "mobx"
import * as Phaser from "phaser-ce"
import { COLOR } from "../../../common/Color"
import { log } from "../../../common/Logging"
import { Align, FontType } from "../../../common/TextUtils"
import { StudentStoreSA } from "../../../react/students/StudentStoreSA"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { ButtonContainer, Buttons, ButtonSize, ButtonType } from "../../generic-sprites/Buttons"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { Rectangle } from "../../generic-sprites/Shape"
import { Audio } from "../../utils/Audio"
import { ColorUtils } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { Transitions } from "../transitions/Transitions"
import { loadAssets, PlaygroundImage } from "./PlaygroundAssets"
import { PlaygroundStore } from "./PlaygroundStore"
import { PlaygroundRectangleColor, PlaygroundRoundText, PlaygroundRoundType } from "./PlaygroundType"
import { KidPromise } from "./sprites/KidPromise"
import { PlaygroundDisplay } from "./sprites/PlaygroundDisplay"
import { RoundNumberContainer } from "./sprites/RoundNumber"
import Color = Phaser.Color


export class PlaygroundState extends Phaser.State {
    background: Phaser.Group
    playgroundGroup: Phaser.Group
    promiseGroup: Phaser.Group
    displayGroup: Phaser.Group // Assets on the final playground display screen

    sideMenu: Rectangle

    nextButton: ButtonContainer
    backButton: ButtonContainer
    doneButton: ButtonContainer

    roundContainer: RoundNumberContainer
    playgroundRound: PlaygroundRoundType
    kidPromise: KidPromise
    displayPromise: KidPromise

    fontStyles: Array<FontType> = [FontType.SILLY_TITLE, FontType.SCIENTIFIC_TITLE, FontType.FUTURISTIC, FontType.HAND_WRITING, FontType.BODY]

    private currentRound = 0
    private round1Played = false
    private round2Played = false
    private round3Played = false
    private round4Played = false
    private round5Played = false

    get round() {
        return this.currentRound
    }

    set round(round: number) {
        if (round >= 0 && round < 5) {
            this.currentRound = round
            this.roundContainer.changeRound(this.round)

            if (this.playgroundGroup) {
                this.playgroundGroup.destroy()
            }

            this.playgroundRound = this.roundData[round]
            this.playgroundGroup = this.game.add.group()
            PhaserText.create(this.game, this.playgroundRound.roundTitle, {
                x: Scale.x(0.05),
                y: Scale.y(0.22),
                group: this.playgroundGroup,
                color: (round === 4) ? COLOR.white : COLOR.black
            })
            this.playgroundRound.roundFunction()
        }

        this.checkIfNarratorPlayed(round)

        round > 0 ? this.backButton.setVisible() : this.backButton.setInvisible()

        if (round >= 4) {
            this.sideMenu.graphics.visible = false
            this.nextButton.setInvisible()
            this.doneButton.setVisible()
        } else {
            this.sideMenu.graphics.visible = true
            this.nextButton.setVisible()
            this.doneButton.setInvisible()
            if (this.kidPromise) {
                this.kidPromise.replacePhrase()
            }
        }
    }

    async preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        PlaygroundStore.newInstance()
        await PhaserUtils.sharedCreateFunctions(this.game)
        if (!PhaserUtils.loggedIn()) {
            PhaserUtils.notLoggedInMessage(this.game)
        } else {
            ColorUtils.setBackgroundColorBlack(this.game)

            // Set playground BACKGROUND image
            this.setBackgroundElements()

            // Set buttons on screen
            this.setNavButtons()

            // Set round data on screen
            this.round = this.currentRound
        }
    }

    private setBackgroundElements() {
        Sprites.add(this.game, PlaygroundImage.BACKGROUND)
        this.background = this.game.add.group()
        this.promiseGroup = this.game.add.group()
        this.sideMenu = new Rectangle(this.game, 0, 0, Scale.x(0.5), Scale.y(1), {lineWidth: 0, fillColor: Color.WHITE, fillAlpha: 0.5, group: this.background})
        new Rectangle(this.game, 0, 0, Scale.x(1), Scale.y(0.2), {fillColor: Color.BLACK, group: this.background})
        PhaserText.create(this.game, "My", {color: COLOR.white, group: this.background, debug: true, x: Scale.x(0.065), y: Scale.y(0.09)})
        PhaserText.create(this.game, "Promise", {color: COLOR.white, group: this.background, debug: true, x: Scale.x(0.03), y: Scale.y(0.145)})
        this.roundContainer = new RoundNumberContainer(this.game, this.background, this.round)

        this.kidPromise = new KidPromise(this.game, this.promiseGroup)
    }

    private setNavButtons() {
        this.nextButton = Buttons.create(this.game, "Next",
            () => this.round += 1,
            {
                x: Scale.x(0.8),
                y: Scale.y(0.9),
                group: this.background,
                buttonType: ButtonType.PRIMARY,
                buttonSize: ButtonSize.WIDE,
                textConfig: {weight: "bold", align: Align.CENTER}
            })

        this.backButton = Buttons.create(this.game, "Back",
            () => this.round -= 1,
            {
                x: Scale.x(0.05),
                y: Scale.y(0.9),
                group: this.background,
                buttonType: ButtonType.PRIMARY,
                buttonSize: ButtonSize.WIDE,
                textConfig: {weight: "bold", align: Align.CENTER},

            })

        this.doneButton = Buttons.create(this.game, "Done",
            () => this.finalPlaygroundDisplay(),
            {
                x: Scale.x(0.8),
                y: Scale.y(0.9),
                group: this.background,
                buttonType: ButtonType.PRIMARY,
                buttonSize: ButtonSize.WIDE,
                textConfig: {weight: "bold", align: Align.CENTER}

            })
        this.doneButton.setInvisible()
        if (this.round === 0) {
            this.backButton.setInvisible()
        }
    }

    private setRound1Data = () => {
        if (!this.kidPromise.selectedText) {
            this.nextButton.disable()
        } else {
            this.nextButton.enable()
        }
        const onClick = (one, two, data) => {
            this.kidPromise.selectedText = data.selected
            this.nextButton.enable()
        }
        const texts = PlaygroundDisplay.textDisplay(this.game, this.playgroundGroup, this.playgroundRound.textList)
        texts.forEach((text) => {
            text.events.onInputDown.add(onClick, this, 0, {selected: text.text})
            if (this.round1Played) {
                text.inputEnabled = true
                text.input.useHandCursor = true
            }
        })
    }

    private setRound2Data = () => {
        if (!this.kidPromise.style) {
            this.nextButton.disable()
        } else {
            this.nextButton.enable()
        }
        const onClick = (one, two, data) => {
            this.kidPromise.selectedStyle = data.selected
            this.nextButton.enable()
        }
        const sampleList = this.fontStyles.map((style: FontType): PlaygroundRoundText => ({text: this.kidPromise.selectedText, style}))
        const texts = PlaygroundDisplay.textDisplay(this.game, this.playgroundGroup, sampleList)
        texts.forEach((text) => {
            text.events.onInputDown.add(onClick, this, 0, {selected: text.font})
            if (this.round2Played) {
                text.inputEnabled = true
                text.input.useHandCursor = true
            }
        })
    }

    private setRound3Data = () => {
        if (!this.kidPromise.color) {
            this.nextButton.disable()
        } else {
            this.nextButton.enable()
        }
        const onClick = (one, two, data) => {
            this.kidPromise.selectedColor = data.selected
            this.nextButton.enable()
        }
        const colors = PlaygroundDisplay.colorDisplay(this.game, this.playgroundGroup, this.playgroundRound.colorGrid)
        colors.forEach((color: PlaygroundRectangleColor) => {
            color.rectangle.invisibleSprite.events.onInputDown.add(onClick, this, 0, {selected: color.color})
            if (this.round3Played) {
                color.rectangle.invisibleSprite.inputEnabled = true
                color.rectangle.invisibleSprite.input.useHandCursor = true
            }
        })
    }

    private setRound4Data = () => {
        if (!this.kidPromise.sprite) {
            this.nextButton.disable()
        } else {
            this.nextButton.enable()
        }
        const onClick = (one, two, data) => {
            this.kidPromise.selectedSprite = data.selected
            this.nextButton.enable()
        }
        const sprites = PlaygroundDisplay.imageDisplay(this.game, this.playgroundGroup, this.playgroundRound.imageGrid)
        sprites.forEach((sprite: Phaser.Sprite) => {
            sprite.events.onInputDown.add(onClick, this, 0, {selected: sprite.key})
            if (this.round4Played) {
                sprite.inputEnabled = true
                sprite.input.useHandCursor = true
            }
        })
    }

    private setRound5Data = () => {
        this.roundContainer.changeSideColor(COLOR.white)
        this.kidPromise.centerPhrase()
    }

    private finalPlaygroundDisplay = () => {

        this.background.destroy()
        this.playgroundGroup.destroy()
        this.kidPromise.savePromise()
        this.kidPromise.destroy()
        if (this.nextButton) {
            this.nextButton.destroy()
        }

        this.displayGroup = this.game.add.group()
        this.displayPromise = new KidPromise(this.game, this.game.add.group())
        const rectangleGroup = this.game.add.group()
        let audioPlayed = false
        let finalNextButton: ButtonContainer
        autorun(() => {
            // Reset the promises displayGroup as state changes and more are added
            this.displayGroup.destroy(true, true)
            finalNextButton = Buttons.create(this.game, "Next",
                () => Transitions.startOutro(this.game, ActivityKey.PLAYGROUND),
                {
                    x: Scale.x(0.8),
                    y: Scale.y(0.9),
                    group: this.displayGroup,
                    buttonType: ButtonType.PRIMARY,
                    buttonSize: ButtonSize.WIDE,
                    textConfig: {weight: "bold", align: Align.CENTER}
                })
            if (!audioPlayed) {
                finalNextButton.disable()
            }
            let promises = PlaygroundStore.instance.findPromises()
            if (promises.length > 20) {
                promises = promises.slice(-20) // Limit to 20 promises so they all fit nicely on screen
            }
            PlaygroundDisplay.reviewDisplay(this.game, this.displayGroup, this.displayPromise, rectangleGroup, promises)
        })

        const audio = this.game.add.audio("round-6.mp3")
        audio.onPlay.addOnce(() => {
            if (finalNextButton) {
                finalNextButton.disable()
            }
        })
        audio.onStop.addOnce(() => {
            if (finalNextButton) {
                audioPlayed = true
                finalNextButton.enable()
            }
        })
        audio.play()
    }

    private checkIfNarratorPlayed = (round: number) => {
        switch (round) {
            case 0:
                if (!this.round1Played) {
                    this.playNarratorAudio()
                    this.round1Played = true
                }
                break
            case 1:
                if (!this.round2Played) {
                    this.playNarratorAudio()
                    this.round2Played = true
                }
                break
            case 2:
                if (!this.round3Played) {
                    this.playNarratorAudio()
                    this.round3Played = true
                }
                break
            case 3:
                if (!this.round4Played) {
                    this.playNarratorAudio()
                    this.round4Played = true
                }
                break
            case 4:
                if (!this.round5Played) {
                    this.playNarratorAudio()
                    this.round5Played = true
                }
                break
            default:
                log.error("No audio available.")
        }
    }

    private playNarratorAudio = () => {
        Audio.add(this.game, this.getRoundAudio(), {
            onPlay: () => {
                this.playgroundGroup.forEach((child) => {
                    child.inputEnabled = false
                })
                this.nextButton.disable()
                this.backButton.disable()
                this.doneButton.disable()
            },
            onStop: () => {
                this.playgroundGroup.forEach((child) => {
                    child.inputEnabled = true
                    child.input.useHandCursor = true
                })
                this.backButton.enable()
                this.doneButton.enable()
                if (this.round === 4) {
                    this.nextButton.enable()
                }
            }
        })
    }

    private getRoundAudio = (): string => `round-${this.round + 1}.mp3`

    private getPlaygroundAudio = (promise: number): string =>
        `playground-promises-${promise}-${StudentStoreSA.instance.isGirl() ? "girl" : "boy"}.mp3`

    private roundData: Array<PlaygroundRoundType> = [
        {
            roundTitle: "Choose your Promise.",
            roundFunction: this.setRound1Data,
            textList: [
                {
                    text: "I will never use tobacco.",
                    audio: this.getPlaygroundAudio(1)
                },
                {
                    text: "I will never use e-cigarettes.",
                    audio: this.getPlaygroundAudio(2)
                },
                {
                    text: "I will never smoke cigarettes.",
                    audio: this.getPlaygroundAudio(3)
                },
                {
                    text: "I will ask my parents or other family members not to smoke or vape around me.",
                    audio: this.getPlaygroundAudio(4)
                },
                {
                    text: "I will be tobacco-free.",
                    audio: this.getPlaygroundAudio(5)
                },
                {
                    text: "I will ask my friends not to start using e-cigarettes.",
                    audio: this.getPlaygroundAudio(6)
                },
                {
                    text: "I will ask my parents or other family members to try to quit using tobacco.",
                    audio: this.getPlaygroundAudio(7)
                },
                {
                    text: "I don’t want to get addicted to tobacco.",
                    audio: this.getPlaygroundAudio(8)
                },
                {
                    text: "I won’t try vaping or smoking again.",
                    audio: this.getPlaygroundAudio(9)
                }
            ]
        },
        {
            roundTitle: "Choose your style.",
            roundFunction: this.setRound2Data,
        },
        {
            roundTitle: "Choose your color.",
            roundFunction: this.setRound3Data,
            colorGrid: [
                {rgb: Color.YELLOW, hex: "#ffff00"},
                {rgb: Color.RED, hex: "#ff0000"},
                {rgb: Color.ORANGE, hex: "#ff9900"},
                {rgb: Color.GREEN, hex: "#00ff00"},
                {rgb: Color.AQUA, hex: "#00ffff"},
                {rgb: Color.VIOLET, hex: "#ff00ff"},
                {rgb: Color.BLUE, hex: "#0000ff"},
                {rgb: Color.WHITE, hex: "#ffffff"}
            ]
        },
        {
            roundTitle: "Choose your picture.",
            roundFunction: this.setRound4Data,
            imageGrid: [
                PlaygroundImage.PURPLE_FLOWER,
                PlaygroundImage.STAR,
                PlaygroundImage.PEACE,
                PlaygroundImage.LIGHTNING,
                PlaygroundImage.PINK_FLOWER,
                PlaygroundImage.PURPLE_STAR,
                PlaygroundImage.PEACE_HAND,
                PlaygroundImage.GUITAR,
                PlaygroundImage.SMILE,
                PlaygroundImage.HEART,
                PlaygroundImage.PLANET,
                PlaygroundImage.SPACESHIP,
                PlaygroundImage.BASKETBALL,
                PlaygroundImage.SOCCER,
                PlaygroundImage.BASEBALL,
                PlaygroundImage.FOOTBALL,
                PlaygroundImage.ALIEN,
                PlaygroundImage.SKATEBOARD,
                PlaygroundImage.RACE_CAR,
                PlaygroundImage.BMX
            ]
        },
        {
            roundTitle: "Your promise.",
            roundFunction: this.setRound5Data,
        }
    ]
}

